import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

const Setup: React.FC = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState<'password' | 'install' | 'admin' | 'organization' | 'complete'>('password')
  const [setupPassword, setSetupPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  
  // Install type selection
  const [installType, setInstallType] = useState<'automated' | 'manual'>('automated')
  const [runMigrations, setRunMigrations] = useState(true)
  const [seedRoles, setSeedRoles] = useState(true)
  const [seedSampleData, setSeedSampleData] = useState(false)

  // Admin user form
  const [adminData, setAdminData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  })

  // Staff organization form
  const [orgData, setOrgData] = useState({
    name: '',
    domain: '',
    industry: '',
    companySize: ''
  })

  useEffect(() => {
    // Check immediately on mount - if setup is complete, redirect right away
    checkSetupStatus()
    
    // Only poll if we're not on the complete step
    const interval = setInterval(() => {
      if (step !== 'complete') {
        checkSetupStatus()
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [step])

  // Auto-redirect when setup is complete
  useEffect(() => {
    if (step === 'complete') {
      // Wait a moment to show the success message, then redirect
      const timer = setTimeout(() => {
        navigate('/login', { replace: true })
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [step, navigate])

  const checkSetupStatus = async () => {
    try {
      const response = await axios.get('/api/setup/status')
      if (response.data.isSetupComplete) {
        setStep('complete')
        // Immediately redirect - don't wait for the useEffect
        setTimeout(() => {
          navigate('/login', { replace: true })
        }, 500)
      }
    } catch (error: any) {
      console.error('Setup check error:', error)
      // If we can't check status, show an error but don't block
      if (error.response?.status === 503) {
        // This might mean setup is required but API is having issues
        setError('Unable to verify setup status. Please check your connection and try again.')
      }
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (setupPassword !== 'Setup4Admin!') {
      setError('Invalid setup password')
      return
    }

    // Check if setup is already complete before proceeding
    setLoading(true)
    setError('')
    try {
      const response = await axios.get('/api/setup/status')
      if (response.data.isSetupComplete) {
        setStep('complete')
        setLoading(false)
        // Will redirect via useEffect
        return
      }
    } catch (error: any) {
      console.error('Setup status check error:', error)
      // Continue with setup if check fails - might be a network issue
    }
    
    setLoading(false)
    setStep('admin')
  }
  
  const handleInstallSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    
    // Validate admin data exists
    if (!adminData.firstName || !adminData.lastName || !adminData.email || !adminData.password) {
      setError('Please complete the admin user details first')
      setLoading(false)
      setStep('admin')
      return
    }
    
    // Validate organization data exists
    if (!orgData.name) {
      setError('Please complete the organization details first')
      setLoading(false)
      setStep('organization')
      return
    }
    
    if (installType === 'manual') {
      if (!runMigrations && !seedRoles && !seedSampleData) {
        setError('Please select at least one option to proceed')
        setLoading(false)
        return
      }
    }
    
    try {
      // Run operations sequentially (migrations must complete before seeding)
      if (installType === 'automated') {
        // Run migrations first
        const migrationResult = await axios.post('/api/setup/run-migrations')
        if (!migrationResult.data.success) {
          setError(migrationResult.data.error || 'Migration failed')
          setLoading(false)
          return
        }
        
        // Then run seeding
        const seedResult = await axios.post('/api/setup/run-seed')
        if (!seedResult.data.success) {
          setError(seedResult.data.error || 'Seeding failed')
          setLoading(false)
          return
        }
      } else {
        // Manual mode - run only selected operations sequentially
        if (runMigrations) {
          const migrationResult = await axios.post('/api/setup/run-migrations')
          if (!migrationResult.data.success) {
            setError(migrationResult.data.error || 'Migration failed')
            setLoading(false)
            return
          }
        }
        
        if (seedRoles) {
          const seedResult = await axios.post('/api/setup/run-seed')
          if (!seedResult.data.success) {
            setError(seedResult.data.error || 'Seeding failed')
            setLoading(false)
            return
          }
        }
        
        if (seedSampleData) {
          const fullSeedResult = await axios.post('/api/setup/run-seed-full')
          if (!fullSeedResult.data.success) {
            setError(fullSeedResult.data.error || 'Full seeding failed')
            setLoading(false)
            return
          }
        }
      }

      // Now complete the setup with admin and organization
      const response = await axios.post('/api/setup/complete', {
        setupPassword: 'Setup4Admin!',
        adminUser: {
          firstName: adminData.firstName,
          lastName: adminData.lastName,
          email: adminData.email,
          password: adminData.password
        },
        staffOrganization: orgData
      })

      if (response.data.success) {
        setStep('complete')
        // Auto-redirect will be handled by the useEffect
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Setup failed'
      const fullError = error.response?.data?.details || error.response?.data?.error || error.message
      console.error('Setup error:', {
        message: errorMessage,
        fullError,
        status: error.response?.status,
        data: error.response?.data
      })
      setError(`Setup failed: ${errorMessage}${fullError && fullError !== errorMessage ? ` (${fullError})` : ''}`)
    } finally {
      setLoading(false)
    }
  }
  
  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!adminData.firstName || !adminData.lastName || !adminData.email || !adminData.password) {
      setError('Please fill in all fields')
      return
    }

    if (adminData.password !== adminData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (adminData.password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setStep('organization')
  }

  if (step === 'complete') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Setup Complete!</h2>
            <p className="text-gray-600 mb-6">Your system is now ready to use. Setup has been completed and cannot be repeated.</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">System Setup</h2>
          <p className="mt-2 text-sm text-gray-600">
            {step === 'password' && 'Enter the setup password to begin'}
            {step === 'install' && 'Choose Installation Type'}
            {step === 'admin' && 'Step 2: Create Admin Account'}
            {step === 'organization' && 'Step 3: Configure Organization'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {step === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Setup Password</label>
              <input
                type="password"
                value={setupPassword}
                onChange={(e) => setSetupPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter setup password"
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Checking...' : 'Continue'}
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (setupPassword !== 'Setup4Admin!') {
                    setError('Please enter the setup password first')
                    return
                  }
                  setLoading(true)
                  setError('')
                  try {
                    const response = await axios.post('/api/setup/mark-complete', {
                      setupPassword: setupPassword
                    })
                    if (response.data.success) {
                      setStep('complete')
                      // Redirect after a moment
                      setTimeout(() => {
                        navigate('/login', { replace: true })
                      }, 2000)
                    } else {
                      setError('Failed to mark setup as complete')
                    }
                  } catch (error: any) {
                    console.error('Mark complete error:', error)
                    console.error('Error response:', error.response?.data)
                    
                    let errorMessage = 'Failed to mark setup as complete'
                    if (error.response?.data) {
                      errorMessage = error.response.data.error || errorMessage
                      const details = error.response.data.details
                      const diagnostics = error.response.data.diagnostics
                      
                      if (details) {
                        errorMessage += `: ${details}`
                      }
                      if (diagnostics) {
                        errorMessage += ` (Staff Orgs: ${diagnostics.staffOrgCount || 0}, Has Admin: ${diagnostics.hasAdminUser ? 'Yes' : 'No'}, Has Admin Role: ${diagnostics.hasAdminRole ? 'Yes' : 'No'})`
                      }
                    } else if (error.message) {
                      errorMessage = `${errorMessage}: ${error.message}`
                    }
                    
                    setError(errorMessage)
                  } finally {
                    setLoading(false)
                  }
                }}
                disabled={loading || !setupPassword}
                className="w-full bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {loading ? 'Processing...' : 'Mark Setup as Complete (if already done)'}
              </button>
            </div>
          </form>
        )}

        {step === 'admin' && (
          <form onSubmit={handleAdminSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name *</label>
                <input
                  type="text"
                  value={adminData.firstName}
                  onChange={(e) => setAdminData({ ...adminData, firstName: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                <input
                  type="text"
                  value={adminData.lastName}
                  onChange={(e) => setAdminData({ ...adminData, lastName: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email *</label>
              <input
                type="email"
                value={adminData.email}
                onChange={(e) => setAdminData({ ...adminData, email: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password *</label>
              <input
                type="password"
                value={adminData.password}
                onChange={(e) => setAdminData({ ...adminData, password: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                minLength={8}
                required
              />
              <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm Password *</label>
              <input
                type="password"
                value={adminData.confirmPassword}
                onChange={(e) => setAdminData({ ...adminData, confirmPassword: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                minLength={8}
                required
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setStep('password')}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
              >
                Back
              </button>
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
              >
                Next
              </button>
            </div>
          </form>
        )}

        {step === 'organization' && (
          <form onSubmit={(e) => {
            e.preventDefault()
            setStep('install')
          }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Organization Name *</label>
              <input
                type="text"
                value={orgData.name}
                onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Domain</label>
              <input
                type="text"
                value={orgData.domain}
                onChange={(e) => setOrgData({ ...orgData, domain: e.target.value })}
                placeholder="example.com"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Industry</label>
              <input
                type="text"
                value={orgData.industry}
                onChange={(e) => setOrgData({ ...orgData, industry: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Size</label>
              <select
                value={orgData.companySize}
                onChange={(e) => setOrgData({ ...orgData, companySize: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Select...</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="500+">500+ employees</option>
              </select>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setStep('admin')}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
              >
                Back
              </button>
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
              >
                Next
              </button>
            </div>
          </form>
        )}

        {step === 'install' && (
          <form onSubmit={handleInstallSubmit} className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Database Setup</h2>
            <p className="text-sm text-gray-600 mb-4">
              Choose how you want to set up your database. Automated install is recommended for new installations.
            </p>

            <div>
              <div className="space-y-2">
                <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="installType"
                    value="automated"
                    checked={installType === 'automated'}
                    onChange={() => setInstallType('automated')}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium">Automated Install (Recommended)</div>
                    <div className="text-sm text-gray-500">Runs migrations and seeds all roles/permissions automatically</div>
                  </div>
                </label>
                <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="installType"
                    value="manual"
                    checked={installType === 'manual'}
                    onChange={() => setInstallType('manual')}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium">Manual Install</div>
                    <div className="text-sm text-gray-500">Choose what to run</div>
                  </div>
                </label>
              </div>
            </div>

            {installType === 'manual' && (
              <div className="border-t pt-4 space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={runMigrations}
                    onChange={(e) => setRunMigrations(e.target.checked)}
                    className="mr-3"
                  />
                  <span className="text-sm">Run Database Migrations (Creates tables)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={seedRoles}
                    onChange={(e) => setSeedRoles(e.target.checked)}
                    className="mr-3"
                  />
                  <span className="text-sm">Seed Roles & Permissions</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={seedSampleData}
                    onChange={(e) => setSeedSampleData(e.target.checked)}
                    className="mr-3"
                  />
                  <span className="text-sm">Seed Sample Data (Optional)</span>
                </label>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setStep('organization')}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Setting up...' : 'Complete Setup'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default Setup

